import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const AYLAR = ['','Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

// ── Konu tanımları ─────────────────────────────────────────────────────────
interface DegerTanim { id: string; ad: string }
interface SatirSeceneği { id: string; ad: string }
interface KonuTanim {
  ad: string;
  irk_sutun_destekli?: boolean;
  satir_secenekler: SatirSeceneği[];
  satir_secenekler_deger: SatirSeceneği[];
  degerler: DegerTanim[];
}

const ISLETME_SATIR: SatirSeceneği[] = [{ id: 'isletme', ad: 'İşletme bazında' }];
const ISLETME_IRK_SATIR: SatirSeceneği[] = [
  { id: 'isletme', ad: 'İşletme bazında' },
  { id: 'irk', ad: 'Irk bazında (tüm işletmeler toplam)' },
  { id: 'isletme_irk', ad: 'İşletme + Irk (satır birleştirmeli)' },
  { id: 'isletme_irk_flat', ad: 'İşletme + Irk (düz, iki sol sütun)' },
];
const ISLETME_TUR_SATIR: SatirSeceneği[] = [
  { id: 'isletme', ad: 'İşletme bazında' },
  { id: 'irk', ad: 'Irk bazında' },
  { id: 'tur', ad: 'Tür bazında (Koyun/Keçi)' },
  { id: 'isletme_irk', ad: 'İşletme + Irk (satır birleştirmeli)' },
  { id: 'isletme_irk_flat', ad: 'İşletme + Irk (düz, iki sol sütun)' },
];
const ISLETME_URUN_SATIR: SatirSeceneği[] = [
  { id: 'isletme', ad: 'İşletme bazında' },
  { id: 'urun', ad: 'Ürün bazında (tüm işletmeler toplam)' },
];
const ISLETME_MAKINE_SATIR: SatirSeceneği[] = [
  { id: 'isletme', ad: 'İşletme bazında' },
  { id: 'makine_turu', ad: 'Makine türü bazında' },
];

const KONU_CONFIG: Record<string, KonuTanim> = {
  buyukbas: {
    ad: '🐄 Büyükbaş',
    irk_sutun_destekli: true,
    satir_secenekler: [
      { id: 'none', ad: 'Irk ayrımı yok' }, { id: 'irk', ad: 'Irk bazında' }],
    satir_secenekler_deger: ISLETME_IRK_SATIR,
    degerler: [
      { id: 'toplam_hayvan', ad: 'Toplam Hayvan' }, { id: 'inek_sayisi', ad: 'İnek' },
      { id: 'duve_sayisi', ad: 'Düve' }, { id: 'boga_sayisi', ad: 'Boğa' },
      { id: 'sut_uretimi_kg', ad: 'Süt (kg)' }, { id: 'buzagi_dogum', ad: 'Doğum' },
    ],
  },
  kucukbas: {
    ad: '🐑 Küçükbaş',
    irk_sutun_destekli: true,
    satir_secenekler: [
      { id: 'none', ad: 'Genel' }, { id: 'irk', ad: 'Irk bazında' }, { id: 'tur', ad: 'Tür (Koyun/Keçi)' }],
    satir_secenekler_deger: ISLETME_TUR_SATIR,
    degerler: [
      { id: 'toplam_hayvan', ad: 'Toplam Hayvan' }, { id: 'koc_teke_sayisi', ad: 'Koç/Teke' },
      { id: 'ana_sayisi', ad: 'Ana Hayvan' }, { id: 'kuzu_oglak_sayisi', ad: 'Kuzu/Oğlak' },
      { id: 'sut_uretimi_lt', ad: 'Süt (lt)' },
    ],
  },
  at: {
    ad: '🐎 At Yetiştiriciliği',
    satir_secenekler: [{ id: 'none', ad: 'Genel' }],
    satir_secenekler_deger: ISLETME_SATIR,
    degerler: [
      { id: 'at_toplam', ad: 'Toplam At' }, { id: 'aygir_sayisi', ad: 'Aygır' },
      { id: 'kisrak_sayisi', ad: 'Kısrak' }, { id: 'tay_erkek', ad: 'Tay (E)' },
      { id: 'tay_disi', ad: 'Tay (D)' }, { id: 'at_dogum', ad: 'Doğum (Tay)' },
    ],
  },
  ari: {
    ad: '🐝 Arıcılık',
    satir_secenekler: [{ id: 'none', ad: 'Genel' }],
    satir_secenekler_deger: ISLETME_SATIR,
    degerler: [
      { id: 'kovan_sayisi', ad: 'Kovan' }, { id: 'bal_uretimi_kg', ad: 'Bal (kg)' },
      { id: 'cam_bali_kg', ad: 'Çam Balı (kg)' }, { id: 'cicek_bali_kg', ad: 'Çiçek Balı (kg)' },
    ],
  },
  ceylan: {
    ad: '🦌 Asya Ceylanı',
    satir_secenekler: [{ id: 'none', ad: 'Genel' }],
    satir_secenekler_deger: ISLETME_SATIR,
    degerler: [
      { id: 'toplam_bas', ad: 'Toplam' }, { id: 'erkek_bas', ad: 'Erkek' },
      { id: 'disi_bas', ad: 'Dişi' }, { id: 'erkek_yavru', ad: 'Yavru (E)' },
      { id: 'disi_yavru', ad: 'Yavru (D)' }, { id: 'dogum_sayisi', ad: 'Doğum' },
    ],
  },
  kopek: {
    ad: '🐕 Çoban Köpeği',
    irk_sutun_destekli: true,
    satir_secenekler: [
      { id: 'none', ad: 'Genel' }, { id: 'irk', ad: 'Irk bazında' }],
    satir_secenekler_deger: ISLETME_IRK_SATIR,
    degerler: [
      { id: 'toplam_bas', ad: 'Toplam' }, { id: 'baba_kopek', ad: 'Baba' },
      { id: 'anne_kopek', ad: 'Anne' }, { id: 'genc_erkek', ad: 'Genç (E)' },
      { id: 'genc_disi', ad: 'Genç (D)' },
    ],
  },
  tarla: {
    ad: '🌾 Tarla Bitkileri',
    satir_secenekler: [
      { id: 'none', ad: 'Genel' }, { id: 'urun', ad: 'Ürün bazında' }],
    satir_secenekler_deger: ISLETME_URUN_SATIR,
    degerler: [
      { id: 'hasat_alani_da', ad: 'Hasat Alanı (da)' },
      { id: 'uretim_kg', ad: 'Üretim (kg)' }, { id: 'verim_kg_da', ad: 'Verim (kg/da)' },
    ],
  },
  yem: {
    ad: '🌿 Yem Bitkileri',
    satir_secenekler: [
      { id: 'none', ad: 'Genel' }, { id: 'urun', ad: 'Ürün bazında' }],
    satir_secenekler_deger: ISLETME_URUN_SATIR,
    degerler: [
      { id: 'alan_da', ad: 'Alan (da)' }, { id: 'uretim_kg', ad: 'Üretim (kg)' },
    ],
  },
  bahce: {
    ad: '🌳 Bahçe Kültürleri',
    satir_secenekler: [
      { id: 'none', ad: 'Genel' }, { id: 'urun', ad: 'Ürün bazında' }],
    satir_secenekler_deger: ISLETME_URUN_SATIR,
    degerler: [
      { id: 'alan_da', ad: 'Alan (da)' }, { id: 'uretim_kg', ad: 'Üretim (kg)' },
    ],
  },
  arazi: {
    ad: '🌱 Arazi Varlığı',
    satir_secenekler: [{ id: 'none', ad: 'Genel' }],
    satir_secenekler_deger: ISLETME_SATIR,
    degerler: [
      { id: 'toplam_arazi_da', ad: 'Toplam Arazi (da)' }, { id: 'tarla_arazisi_da', ad: 'Tarla (da)' },
      { id: 'otlakiye_da', ad: 'Otlakiye (da)' }, { id: 'sulanan_arazi_da', ad: 'Sulanan (da)' },
    ],
  },
  makine: {
    ad: '🚜 Makine & Ekipman',
    satir_secenekler: [
      { id: 'none', ad: 'Genel' }, { id: 'makine_turu', ad: 'Makine türü bazında' }],
    satir_secenekler_deger: ISLETME_MAKINE_SATIR,
    degerler: [
      { id: 'adet', ad: 'Adet' }, { id: 'toplam_guc_kw', ad: 'Güç (kW)' },
    ],
  },
  tasit: {
    ad: '🚗 Taşıt Varlığı',
    satir_secenekler: [{ id: 'none', ad: 'Genel' }],
    satir_secenekler_deger: ISLETME_SATIR,
    degerler: [
      { id: 'tasit_toplam', ad: 'Toplam Taşıt' }, { id: 'hizmet_otosu', ad: 'Hizmet Otosu' },
      { id: 'kamyon', ad: 'Kamyon' }, { id: 'kamyonet', ad: 'Kamyonet' },
      { id: 'minibus', ad: 'Minibüs' },
    ],
  },
  tesis: {
    ad: '🏗️ Tesis Varlığı',
    satir_secenekler: [{ id: 'none', ad: 'Genel' }],
    satir_secenekler_deger: ISLETME_SATIR,
    degerler: [
      { id: 'silo_ton', ad: 'Silo (ton)' }, { id: 'hangar_m2', ad: 'Hangar (m²)' },
      { id: 'yem_ambar_m3', ad: 'Yem Ambarı (m³)' },
    ],
  },
};

const YIL_SECENEKLER = [2020, 2021, 2022, 2023, 2024, 2025, 2026];

const fmt = (v: any) =>
  typeof v === 'number'
    ? Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })
    : (v ?? '');

// ── Bileşen ────────────────────────────────────────────────────────────────
export default function PivotRapor() {
  const [isletmeler, setIsletmeler] = useState<any[]>([]);
  const [konu, setKonu] = useState('buyukbas');
  const [yillar, setYillar] = useState<number[]>([new Date().getFullYear() - 1]);
  const [ay, setAy] = useState(0);
  const [sutunEkseni, setSutunEkseni] = useState('isletme');
  const [satirEkseni, setSatirEkseni] = useState('none');
  const [secilenDegerler, setSecilenDegerler] = useState<string[]>(['toplam_hayvan']);
  const [secilenIsletmeler, setSecilenIsletmeler] = useState<number[]>([]);
  const [tumIsletmeler, setTumIsletmeler] = useState(true);
  const [pivotData, setPivotData] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    axios.get(`${API}/api/rapor/isletmeler`, { headers: hdrs() }).then(r => setIsletmeler(r.data));
  }, []);

  // Konu veya sütun ekseni değişince satır eksenini ve varsayılan değerleri sıfırla
  useEffect(() => {
    const cfg = KONU_CONFIG[konu];
    if (cfg) {
      setSecilenDegerler([cfg.degerler[0].id]);
      const secenekler = sutunEkseni === 'deger' ? cfg.satir_secenekler_deger : cfg.satir_secenekler;
      setSatirEkseni(secenekler[0].id);
    }
  }, [konu, sutunEkseni]);

  const fetchPivot = useCallback(async () => {
    if (!secilenDegerler.length) return;
    setYukleniyor(true);
    try {
      const res = await axios.post(`${API}/api/rapor/pivot`, {
        konu,
        yillar,
        ay: ay || null,
        sutun_ekseni: sutunEkseni,
        satir_ekseni: satirEkseni,
        degerler: secilenDegerler,
        isletme_ids: tumIsletmeler ? [] : secilenIsletmeler,
      }, { headers: hdrs() });
      setPivotData(res.data);
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  }, [konu, yillar, ay, sutunEkseni, satirEkseni, secilenDegerler, secilenIsletmeler, tumIsletmeler]);

  // Debounced auto-fetch
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(fetchPivot, 700);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [fetchPivot]);

  const toggleYil = (y: number) =>
    setYillar(prev => prev.includes(y) ? prev.filter(x => x !== y) : [...prev, y].sort());

  const toggleDeger = (id: string) =>
    setSecilenDegerler(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Excel ──────────────────────────────────────────────────────────────
  const excelIndir = () => {
    if (!pivotData) return;
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];

    const konuCfg = KONU_CONFIG[konu];
    const baslik = `TİGEM — ${konuCfg?.ad.replace(/^[^ ]+ /, '')} Pivot Raporu`;
    const sutunAd = sutunEkseni === 'isletme' ? 'İşletme' : sutunEkseni === 'yil' ? 'Yıl'
      : sutunEkseni === 'isletme_yil' ? 'İşletme/Yıl' : 'Değer Başlıkları';
    const altBaslik = `Dönem: ${yillar.join(', ')} ${ay ? AYLAR[ay] : 'Yıllık'} | Sütun: ${sutunAd}`;

    wsData.push([baslik]);
    wsData.push([altBaslik]);
    wsData.push([]);

    // Başlık satırı
    const headerRow: string[] = [];
    if (pivotData.satir_grubu_var) headerRow.push('Grup');
    headerRow.push('Değer');
    headerRow.push(...pivotData.sutunlar);
    wsData.push(headerRow);

    // Veri satırları
    let prevGrup: string | null = undefined as any;
    for (const satir of pivotData.satirlar) {
      const row: any[] = [];
      if (pivotData.satir_grubu_var) {
        row.push(satir.grup !== prevGrup ? satir.grup : '');
        prevGrup = satir.grup;
      }
      row.push(satir.deger_adi);
      row.push(...satir.hucreler);
      wsData.push(row);
    }

    // Genel toplam
    wsData.push([]);
    prevGrup = undefined as any;
    for (const satir of pivotData.genel_toplam) {
      const row: any[] = [];
      if (pivotData.satir_grubu_var) row.push(satir === pivotData.genel_toplam[0] ? 'GENEL TOPLAM' : '');
      row.push(satir === pivotData.genel_toplam[0] && !pivotData.satir_grubu_var ? 'GENEL TOPLAM' : satir.deger_adi);
      if (!pivotData.satir_grubu_var && satir !== pivotData.genel_toplam[0]) {
        // overwrite first col
        row[row.length - 1] = satir.deger_adi;
      }
      row.push(...satir.hucreler);
      wsData.push(row);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Sütun genişlikleri
    const colCount = headerRow.length;
    ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
      wch: i < (pivotData.satir_grubu_var ? 2 : 1) ? 24 : 14
    }));

    const donem = `${yillar.join('-')}${ay ? '_' + AYLAR[ay] : ''}`;
    const sheetName = `${pivotData.konu_adi}`.substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `TIGEM_Pivot_${pivotData.konu}_${donem}.xlsx`);
  };

  // ── Pivot tablo render ─────────────────────────────────────────────────
  const renderPivot = () => {
    if (!pivotData || !pivotData.sutunlar?.length) return null;

    const colBg = (ci: number) => ci === pivotData.sutunlar.length - 1 ? '#eaf0fb' : 'transparent';
    const numCell = (v: number, ci: number): React.CSSProperties => ({
      padding: '7px 12px', borderBottom: '1px solid #eee',
      textAlign: 'right', whiteSpace: 'nowrap',
      background: colBg(ci),
      fontWeight: ci === pivotData.sutunlar.length - 1 ? 700 : 400,
      color: ci === pivotData.sutunlar.length - 1 ? '#1a5276' : '#222',
    });

    // Sütun eksen başlığı
    const satirBaslik =
      sutunEkseni === 'irk_deger' ? 'İşletme' :
      sutunEkseni === 'deger' ? (
        satirEkseni === 'isletme' ? 'İşletme' :
        (satirEkseni === 'isletme_irk' || satirEkseni === 'isletme_irk_flat') ? 'Irk' :
        satirEkseni === 'irk' ? 'Irk' :
        satirEkseni === 'tur' ? 'Tür' :
        satirEkseni === 'urun' ? 'Ürün' :
        satirEkseni === 'makine_turu' ? 'Makine Türü' : 'Değer'
      ) : 'Değer';

    const grupBaslik =
      (satirEkseni === 'isletme_irk' || satirEkseni === 'isletme_irk_flat') ? 'İşletme' :
      satirEkseni === 'irk' ? 'Irk' :
      satirEkseni === 'tur' ? 'Tür' :
      satirEkseni === 'urun' ? 'Ürün' :
      satirEkseni === 'isletme' ? 'İşletme' : 'Grup';

    // ── Düz iki-sütun modu (isletme_irk_flat) ──────────────────────────
    if (pivotData.satir_grubu_flat) {
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={thStyle('#0d3b66')}>İşletme</th>
                <th style={thStyle('#0d3b66')}>Irk</th>
                {pivotData.sutunlar.map((s: string, i: number) => (
                  <th key={i} style={thStyle(i === pivotData.sutunlar.length - 1 ? '#154360' : '#1a5276')}>{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pivotData.satirlar.map((satir: any, ri: number) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#f8fafb' }}>
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #eee',
                    fontWeight: 600, color: '#1a5276', borderRight: '1px solid #ddd',
                    whiteSpace: 'nowrap' }}>
                    {satir.grup || '—'}
                  </td>
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #eee',
                    color: '#444', borderRight: '1px solid #eee', whiteSpace: 'nowrap' }}>
                    {satir.deger_adi}
                  </td>
                  {satir.hucreler.map((v: number, ci: number) => (
                    <td key={ci} style={numCell(v, ci)}>{fmt(v)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan={99} style={{ height: 4 }} /></tr>
              {pivotData.genel_toplam.map((satir: any, ri: number) => (
                <tr key={ri} style={{ background: '#1a5276' }}>
                  {ri === 0 && (
                    <td colSpan={2} style={{ padding: '7px 12px', color: 'white',
                      fontWeight: 800, borderRight: '1px solid rgba(255,255,255,0.3)' }}>
                      GENEL TOPLAM
                    </td>
                  )}
                  {satir.hucreler.map((v: number, ci: number) => (
                    <td key={ci} style={{ padding: '7px 12px', textAlign: 'right',
                      color: 'white', fontWeight: 700, whiteSpace: 'nowrap',
                      background: ci === pivotData.sutunlar.length - 1 ? '#0d3b66' : 'transparent' }}>
                      {fmt(v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      );
    }

    // ── Standart mod (grup + değer satırı veya tek sütun) ──────────────
    const grouped: { grup: string | null; rows: any[] }[] = [];
    let cur: { grup: string | null; rows: any[] } | null = null;
    for (const s of pivotData.satirlar) {
      if (!cur || cur.grup !== s.grup) {
        cur = { grup: s.grup, rows: [] };
        grouped.push(cur);
      }
      cur.rows.push(s);
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }}>
          <thead>
            <tr>
              {pivotData.satir_grubu_var && (
                <th style={thStyle('#0d3b66')}>{grupBaslik}</th>
              )}
              <th style={thStyle('#0d3b66')}>{satirBaslik}</th>
              {pivotData.sutunlar.map((s: string, i: number) => (
                <th key={i} style={thStyle(i === pivotData.sutunlar.length - 1 ? '#154360' : '#1a5276')}>
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((g, gi) => (
              g.rows.map((satir, ri) => (
                <tr key={`${gi}-${ri}`} style={{ background: ri % 2 === 0 ? 'white' : '#f8fafb' }}>
                  {ri === 0 && pivotData.satir_grubu_var && (
                    <td rowSpan={g.rows.length}
                      style={{ padding: '7px 12px', fontWeight: 700, borderBottom: '1px solid #ddd',
                        background: '#eaf0fb', borderRight: '2px solid #c5d8f0',
                        verticalAlign: 'middle', maxWidth: 160, color: '#1a5276' }}>
                      {g.grup || '—'}
                    </td>
                  )}
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #eee',
                    whiteSpace: 'nowrap', color: '#444', borderRight: '1px solid #eee' }}>
                    {satir.deger_adi}
                  </td>
                  {satir.hucreler.map((v: number, ci: number) => (
                    <td key={ci} style={numCell(v, ci)}>{fmt(v)}</td>
                  ))}
                </tr>
              ))
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={99} style={{ height: 4 }} /></tr>
            {pivotData.genel_toplam.map((satir: any, ri: number) => (
              <tr key={ri} style={{ background: '#1a5276' }}>
                {ri === 0 && pivotData.satir_grubu_var && (
                  <td rowSpan={pivotData.genel_toplam.length}
                    style={{ padding: '7px 12px', color: 'white', fontWeight: 800,
                      verticalAlign: 'middle', borderRight: '2px solid rgba(255,255,255,0.3)' }}>
                    GENEL TOPLAM
                  </td>
                )}
                {ri === 0 && !pivotData.satir_grubu_var && (
                  <td style={{ padding: '7px 12px', color: 'white', fontWeight: 800,
                    borderRight: '1px solid rgba(255,255,255,0.3)' }}>
                    GENEL TOPLAM
                  </td>
                )}
                {(ri > 0 || pivotData.satir_grubu_var) && (
                  <td style={{ padding: '7px 12px', color: 'rgba(255,255,255,0.85)',
                    borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                    {satir.deger_adi}
                  </td>
                )}
                {satir.hucreler.map((v: number, ci: number) => (
                  <td key={ci} style={{ padding: '7px 12px', textAlign: 'right',
                    color: 'white', fontWeight: 700, whiteSpace: 'nowrap',
                    background: ci === pivotData.sutunlar.length - 1 ? '#0d3b66' : 'transparent' }}>
                    {fmt(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
    );
  };

  const cfg = KONU_CONFIG[konu];

  return (
    <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 80px)' }}>
      <style>{`
        @media print {
          body.pivot-yazdir .topbar, body.pivot-yazdir .navbar,
          body.pivot-yazdir .subbar, body.pivot-yazdir .sidebar,
          body.pivot-yazdir .pivot-sol { display: none !important; }
          body.pivot-yazdir .pivot-container { display: block !important; height: auto !important; }
          body.pivot-yazdir .pivot-sag { width: 100% !important; height: auto !important; overflow: visible !important; }
        }
      `}</style>

      {/* ── SOL PANEL ─────────────────────────────────── */}
      <div className="pivot-sol" style={{ width: 280, background: 'white', borderRadius: 6,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'auto', flexShrink: 0,
        display: 'flex', flexDirection: 'column' }}>

        <div style={{ background: '#1a5276', color: 'white', padding: '12px 16px',
          fontSize: 15, fontWeight: 'bold', flexShrink: 0 }}>
          📐 Pivot Rapor
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 0 8px 0' }}>

          {/* 1. Veri Kaynağı */}
          <Section no="1" title="Veri Kaynağı">
            <select value={konu} onChange={e => setKonu(e.target.value)}
              style={selectStyle}>
              {Object.entries(KONU_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.ad}</option>
              ))}
            </select>
          </Section>

          {/* 2. Dönem */}
          <Section no="2" title="Dönem">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {YIL_SECENEKLER.map(y => (
                <button key={y} onClick={() => toggleYil(y)}
                  style={{ padding: '3px 9px', borderRadius: 4, fontSize: 12, cursor: 'pointer', border: 'none',
                    background: yillar.includes(y) ? '#1a5276' : '#f0f0f0',
                    color: yillar.includes(y) ? 'white' : '#333' }}>
                  {y}
                </button>
              ))}
            </div>
            <select value={ay} onChange={e => setAy(Number(e.target.value))} style={selectStyle}>
              <option value={0}>Tüm Yıl</option>
              {AYLAR.slice(1).map((a, i) => <option key={i + 1} value={i + 1}>{a}</option>)}
            </select>
          </Section>

          {/* 3. Sütunlarda Göster */}
          <Section no="3" title="Sütunlarda Göster">
            {[
              { id: 'isletme', ad: 'İşletme adları' },
              { id: 'yil', ad: 'Yıllar (karşılaştırma)' },
              { id: 'isletme_yil', ad: 'İşletme × Yıl' },
              { id: 'deger', ad: 'Değer başlıkları (metrikler yatayda)' },
              ...(cfg.irk_sutun_destekli
                ? [{ id: 'irk_deger', ad: 'Irk × Değer (işletme satırda, ırk bazında sütunlar)' }]
                : []),
            ].map(s => (
              <label key={s.id} style={radioLabelStyle(sutunEkseni === s.id)}>
                <input type="radio" name="sutun" checked={sutunEkseni === s.id}
                  onChange={() => setSutunEkseni(s.id)} style={{ marginRight: 6 }} />
                {s.ad}
              </label>
            ))}
            {sutunEkseni === 'yil' && yillar.length < 2 && (
              <div style={{ fontSize: 11, color: '#e67e22', marginTop: 4 }}>
                ⚠ Yıl karşılaştırması için üstten birden fazla yıl seçin
              </div>
            )}
            {sutunEkseni === 'deger' && (
              <div style={{ fontSize: 11, color: '#1a5276', marginTop: 4, background: '#eaf0fb',
                padding: '5px 8px', borderRadius: 4 }}>
                ℹ Seçilen metrikler sütun başlığı olur; satırda işletme/ırk görebilirsiniz
              </div>
            )}
            {sutunEkseni === 'irk_deger' && (
              <div style={{ fontSize: 11, color: '#1a5276', marginTop: 4, background: '#eaf0fb',
                padding: '5px 8px', borderRadius: 4 }}>
                ℹ Her ırk için seçilen metrikler ayrı sütun olur; işletmeler solda satır olur
              </div>
            )}
          </Section>

          {/* 4. Satırlarda Göster / Satır Kırılımı */}
          {(() => {
            if (sutunEkseni === 'irk_deger') return null; // bu modda satır eksen sabittir
            const secenekler = sutunEkseni === 'deger'
              ? cfg.satir_secenekler_deger
              : cfg.satir_secenekler;
            const baslik = sutunEkseni === 'deger' ? 'Satırlarda Göster' : 'Satır Kırılımı';
            if (secenekler.length <= 1 && sutunEkseni !== 'deger') return null;
            return (
              <Section no="4" title={baslik}>
                {secenekler.map(s => (
                  <label key={s.id} style={radioLabelStyle(satirEkseni === s.id)}>
                    <input type="radio" name="satir" checked={satirEkseni === s.id}
                      onChange={() => setSatirEkseni(s.id)} style={{ marginRight: 6 }} />
                    {s.ad}
                  </label>
                ))}
              </Section>
            );
          })()}

          {/* 5. Değerler */}
          <Section no="5" title={sutunEkseni === 'deger' ? 'Sütun Değerleri (Metrikler)' : 'Değerler (Sayısal)'}>
            {cfg.degerler.map(d => (
              <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 7,
                fontSize: 13, padding: '4px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={secilenDegerler.includes(d.id)}
                  onChange={() => toggleDeger(d.id)} />
                {d.ad}
              </label>
            ))}
            <button onClick={() => setSecilenDegerler(cfg.degerler.map(d => d.id))}
              style={{ marginTop: 4, fontSize: 11, color: '#1a5276', background: 'none',
                border: 'none', cursor: 'pointer', padding: 0 }}>
              Tümünü seç
            </button>
          </Section>

          {/* 6. İşletme Filtresi */}
          <Section no="6" title="İşletme Filtresi">
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13,
              marginBottom: 6, cursor: 'pointer' }}>
              <input type="radio" checked={tumIsletmeler}
                onChange={() => { setTumIsletmeler(true); setSecilenIsletmeler([]); }} />
              Tüm İşletmeler
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={!tumIsletmeler} onChange={() => setTumIsletmeler(false)} />
              Seçili İşletmeler
            </label>
            {!tumIsletmeler && (
              <div style={{ marginTop: 6, maxHeight: 180, overflow: 'auto',
                border: '1px solid #eee', borderRadius: 4, padding: 6 }}>
                {isletmeler.map(i => (
                  <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 5,
                    fontSize: 12, marginBottom: 3, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={secilenIsletmeler.includes(i.id)}
                      onChange={() => setSecilenIsletmeler(prev =>
                        prev.includes(i.id) ? prev.filter(x => x !== i.id) : [...prev, i.id]
                      )} />
                    <strong>{i.kod}</strong> {i.ad.replace(' Tarım İşletmesi', '')}
                  </label>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Excel / PDF butonları */}
        {pivotData && (
          <div style={{ padding: '10px 12px', borderTop: '1px solid #eee', flexShrink: 0 }}>
            <button onClick={excelIndir}
              style={{ width: '100%', background: '#27ae60', color: 'white',
                border: 'none', borderRadius: 4, padding: '9px 0', fontSize: 13,
                cursor: 'pointer', marginBottom: 6, fontWeight: 600 }}>
              📥 Excel İndir
            </button>
            <button onClick={() => {
              document.body.classList.add('pivot-yazdir');
              window.print();
              setTimeout(() => document.body.classList.remove('pivot-yazdir'), 1000);
            }}
              style={{ width: '100%', background: '#c0392b', color: 'white',
                border: 'none', borderRadius: 4, padding: '8px 0', fontSize: 13, cursor: 'pointer' }}>
              🖨️ PDF Kaydet
            </button>
          </div>
        )}
      </div>

      {/* ── SAĞ PANEL ─────────────────────────────────── */}
      <div className="pivot-sag" style={{ flex: 1, background: 'white', borderRadius: 6,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'auto', display: 'flex',
        flexDirection: 'column' }}>

        {yukleniyor && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: '#999', fontSize: 15 }}>
            ⏳ Pivot tablo oluşturuluyor...
          </div>
        )}

        {!yukleniyor && (!pivotData || !pivotData.sutunlar?.length) && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', color: '#999' }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>📐</div>
            <div style={{ fontSize: 16 }}>Soldan veri kaynağı ve değerleri seçin</div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#bbb' }}>
              Seçimler yapıldıkça tablo otomatik güncellenir
            </div>
          </div>
        )}

        {!yukleniyor && pivotData && pivotData.sutunlar?.length > 0 && (
          <div style={{ padding: 20 }}>
            {/* Rapor başlığı */}
            <div style={{ display: 'flex', alignItems: 'flex-start',
              justifyContent: 'space-between', borderBottom: '3px solid #1a5276',
              paddingBottom: 12, marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, color: '#1a5276', fontSize: 20 }}>
                  {pivotData.konu_adi} — Pivot Raporu
                </h2>
                <div style={{ fontSize: 13, color: '#888', marginTop: 5 }}>
                  {yillar.join(', ')} yılı
                  {ay ? ` · ${AYLAR[ay]}` : ' · Yıllık'}
                  {' · Sütun: '}
                  {sutunEkseni === 'isletme' ? 'İşletme'
                    : sutunEkseni === 'yil' ? 'Yıl'
                    : sutunEkseni === 'isletme_yil' ? 'İşletme × Yıl'
                    : sutunEkseni === 'irk_deger' ? 'Irk × Değer'
                    : 'Değerler (Metrik)'}
                  {(sutunEkseni === 'deger' || sutunEkseni === 'irk_deger') && ` · Satır: ${
                    sutunEkseni === 'irk_deger' ? 'İşletme'
                    : satirEkseni === 'isletme' ? 'İşletme'
                    : satirEkseni === 'isletme_irk' ? 'İşletme + Irk (gruplu)'
                    : satirEkseni === 'isletme_irk_flat' ? 'İşletme + Irk (düz)'
                    : satirEkseni === 'irk' ? 'Irk'
                    : satirEkseni === 'tur' ? 'Tür'
                    : satirEkseni === 'urun' ? 'Ürün'
                    : satirEkseni
                  }`}
                  {!tumIsletmeler && ` · ${secilenIsletmeler.length} işletme`}
                  {' · '}{new Date().toLocaleDateString('tr-TR')}
                </div>
              </div>
              {/* Özet sayaç */}
              <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                <PivotBadge label="Sütun" value={pivotData.sutunlar.length - 1} />
                <PivotBadge label="Satır" value={pivotData.satirlar.length} />
              </div>
            </div>

            {renderPivot()}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Yardımcı stiller / bileşenler ─────────────────────────────────────────

function thStyle(bg: string): React.CSSProperties {
  return {
    padding: '9px 12px', background: bg, color: 'white',
    textAlign: 'left', whiteSpace: 'nowrap', fontSize: 12,
    fontWeight: 700, borderRight: '1px solid rgba(255,255,255,0.2)',
    position: 'sticky', top: 0, zIndex: 1,
  };
}

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', border: '1px solid #ddd',
  borderRadius: 4, fontSize: 13, background: 'white',
};

function radioLabelStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', fontSize: 13, padding: '5px 8px',
    borderRadius: 4, marginBottom: 3, cursor: 'pointer',
    background: active ? '#eaf0fb' : 'transparent',
    fontWeight: active ? 600 : 400,
  };
}

function Section({ no, title, children }: { no: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#1a5276', marginBottom: 8,
        textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {no}. {title}
      </div>
      {children}
    </div>
  );
}

function PivotBadge({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#1a5276' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#888' }}>{label}</div>
    </div>
  );
}
